import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { BsFillCloudArrowUpFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, GridItem, Icon, Input, Text } from '@chakra-ui/react';

import { analyticsIds } from '../../../../constants/analyticsIds';
import { Statuses } from '../../../../constants/statuses';
import { createErrorToast } from '../../../../redux/modules/app';
import { createJobAttachment, selectCurrentJobAttachments, selectJobProfile } from '../../../../redux/modules/jobs';
import colors from '../../../../styles/theme/foundation/colors';

const AttachmentUploadGridItem = () => {
  const dispatch = useDispatch();
  const attachments = useSelector(selectCurrentJobAttachments);

  const attachmentsList = useMemo(() => {
    if (!attachments || typeof attachments !== 'object') {
      return [];
    }

    return attachments;
  }, [attachments]);

  const onDrop = acceptedFiles => {
    if ((attachmentsList.length + acceptedFiles.length) > 4) {
      return dispatch(createErrorToast({
        description: 'The maximum number of attachments for projects is 4.',
        title: 'Please select fewer attachments to upload.',
        duration: 5000
      }));
    }

    return dispatch(createJobAttachment(acceptedFiles));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const { status } = useSelector(selectJobProfile);
  const validAttachments = attachmentsList.filter(a => [Statuses.Uploading, Statuses.Active].includes(a.status));

  const createAttachment = event => {
    if ((attachmentsList.length + event.target.files.length) > 4) {
      return dispatch(createErrorToast({
        description: 'The maximum number of attachments for projects is 4.',
        title: 'Please select fewer attachments to upload.',
        duration: 5000
      }));
    }

    const files = Object.values(event.target.files);
    return dispatch(createJobAttachment(files));
  };

  if (attachmentsList.length > 3) return null;

  return (
    <GridItem
      {...getRootProps()}
      color
      _hover={{ cursor: 'pointer' }}
      alignItems="center"
      border={`1px dashed ${colors.primaryScale[600]}`}
      borderRadius="16px"
      display={status === Statuses.Archived ? 'none' : 'flex'}
      gridColumn={`span ${validAttachments.length % 2 ? 1 : 2}`}
      height="150px"
      justifyContent="center"
      overflow="hidden"
      padding="5px"
      pos="relative"
    >
      <Input
        {...getInputProps()}
        borderRadius="16px"
        bottom={0}
        data-analytics-id={analyticsIds.projectDetailsPage.editProjectForm.attachmentsButton}
        left={0}
        pos="absolute"
        right={0}
        top={0}
        onChange={createAttachment}
      />
      <Flex
        alignItems="center"
        bottom={0}
        flexDir="column"
        justifyContent="center"
        left={0}
        pointerEvents="none"
        pos="absolute"
        right={0}
        top={0}
      >
        <Icon as={BsFillCloudArrowUpFill} boxSize={10} color={colors.primaryScale[600]} />
        <Text fontSize="xs" textAlign="center">
          Drop files here or
          {' '}
          <Text as="span" color={colors.primaryScale[600]} fontWeight="bold">Browse</Text>
        </Text>
      </Flex>
    </GridItem>
  );
};

export default AttachmentUploadGridItem;
